/**
 * Conference timezone configuration.
 * All session times are displayed and entered in this timezone.
 */
export const CONFERENCE_TIMEZONE = 'America/New_York';

/**
 * Convert a datetime-local input value (interpreted as conference timezone) to ISO string.
 * datetime-local gives "2026-03-20T09:00" with no timezone info.
 * We interpret it as Eastern time and convert to UTC for the backend.
 */
export function localInputToISO(datetimeLocalValue: string): string {
  if (!datetimeLocalValue) return '';
  const parts = datetimeLocalValue.split('T');
  const [year, month, day] = parts[0].split('-').map(Number);
  const [hour, minute] = parts[1].split(':').map(Number);

  // Find the UTC offset for Eastern at this date/time
  const tempDate = new Date(Date.UTC(year, month - 1, day, hour, minute));
  const utcStr = tempDate.toLocaleString('en-US', { timeZone: 'UTC' });
  const easternStr = tempDate.toLocaleString('en-US', { timeZone: CONFERENCE_TIMEZONE });
  const offsetMs = new Date(utcStr).getTime() - new Date(easternStr).getTime();

  // Actual UTC = input time + offset
  const actualUTC = new Date(Date.UTC(year, month - 1, day, hour, minute) + offsetMs);
  return actualUTC.toISOString();
}

/**
 * Convert an ISO/UTC date string to a datetime-local value in conference timezone.
 * Used when populating form inputs for editing.
 */
export function isoToLocalInput(isoString: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: CONFERENCE_TIMEZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const get = (type: string) => parts.find(p => p.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/**
 * Format a date string for display in conference timezone.
 */
export function formatConferenceDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    timeZone: CONFERENCE_TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format just the time portion in conference timezone.
 */
export function formatConferenceTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString('en-US', {
    timeZone: CONFERENCE_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format just the date portion in conference timezone.
 */
export function formatConferenceDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    timeZone: CONFERENCE_TIMEZONE,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}
